// ═══════════════════════════════════════════════════════════════════
//  THE WEEK GRID
//
//  One person's week: a column per weekday, a row per bookable slot.
//  Pure, like dayGrid — no fetching, no clock, no browser time zone.
//
//  It does not lay anything out itself. Every column here is the same
//  person on a different day, so each one is `buildDayGrid` for that day
//  with a single column, and the week is those days keyed by date. The
//  overlap, spanning, off-slot and unavailability rules stay in one place
//  and stay tested there.
// ═══════════════════════════════════════════════════════════════════

import type { BookingDetails, HostAvailability, StaffScheduleSite } from "../bookingCalendarApi"
import { buildDayGrid, unavailabilityByColumn } from "./dayGrid"
import type { BusinessHours, SlotData, UnavailabilityMark } from "./dayGrid"
import type { BookingSeasonBounds } from "./bookingSeason"
import type { CalendarDate, WeekDate } from "./siteTime"

/** The default appointment length when the caller's sites report none. Matches useTeamDay. */
const FALLBACK_SLOT_MINUTES = 30

/** The column id every day is built under. Internal — a week's columns are dates, not people. */
const HOST_COLUMN = "me"

/** One week's cells, keyed by ISO date. Every day has the same row count. */
export interface WeekSchedule {
  [isoDate: CalendarDate]: SlotData[]
}

export interface BuildWeekGridInput {
  /** The days to draw, in order. Mon–Fri for the staff week. */
  weekDates: WeekDate[]
  /** Every booking in the window; each day takes the ones that fall on it. */
  bookings: BookingDetails[]
  /** Whose week it is. Only bookings they host draw. */
  calUserId: number
  slotMinutes: number
  timeZone: string
  /** When they are bookable, already merged across their sites. Undefined means "not known". */
  availability: HostAvailability[] | undefined
  /** The off-slot (Managed) event types of the sites in view — see `BuildDayGridInput`. */
  offSlotEventTypeIds?: readonly number[]
  /** The private event types of the sites in view — see `BuildDayGridInput`. */
  privateEventTypeIds?: readonly number[]
  /** The window each day draws — see `businessHoursFor`. Omitted draws a main office's day. */
  businessHours?: BusinessHours
  /** When the public may book. Undefined means "not known" and marks nothing; see `buildDayGrid`. */
  bookingSeason?: BookingSeasonBounds
}

/**
 * The one availability answer the grid reads for the sites in view.
 *
 * Somebody hosting at two offices gets an answer per office, and the grid shades every row outside
 * the ranges it is given — so handing it a single site's ranges would black out the days they spend
 * at the other. The union is what "when is this person bookable anywhere" means. With one site
 * selected there is nothing to union, and this is that site's own answer.
 *
 * Returns undefined — "not known" — rather than an empty list or a partial union in two cases, and
 * for the same reason both times: the grid reads what it is given as the whole truth.
 *
 * - Nothing was read. An empty list would say "bookable at no hour", which is a far stronger claim
 *   than "nobody asked".
 * - Any site in view failed to answer. A union of the sites that did report reports their hours as
 *   the whole of this person's week, so the failed site's working hours shade as time off and its
 *   real appointments draw as conflicts — a confident wrong answer where the truth is that this part
 *   of the week is unknown.
 */
export function weekAvailability(
  sites: StaffScheduleSite[],
  calUserId: number,
): HostAvailability[] | undefined {
  if (sites.some(site => site.availabilityUnavailable)) return undefined

  const mine = sites.flatMap(site => site.availability.filter(host => host.userId === calUserId))
  if (mine.length === 0) return undefined

  return [{
    userId: calUserId,
    dateRanges: mine.flatMap(host => host.dateRanges),
    // Kept whole rather than deduplicated: an override is why a row is dead, and two sites closing
    // the same afternoon for different reasons are two things a person may need to read.
    overrides: mine.flatMap(host => host.overrides),
    // Every site's capped days (AB#606). The count behind them is the person's across all sites, so
    // a day one site calls capped labels only the rows no site's ranges cover — which is what the
    // grid does with it anyway.
    cappedDays: [...new Set(mine.flatMap(host => host.cappedDays ?? []))],
  }]
}

/**
 * The row height for a week that may span more than one site.
 *
 * The smallest length among them, because a 15-minute appointment cannot be drawn on a 30-minute
 * axis without landing off-grid — where the day view could take its cadence from the one team it
 * shows, this has to accommodate all of them.
 */
export function weekSlotMinutes(sites: StaffScheduleSite[]): number {
  const lengths = sites.map(site => site.lengthInMinutes).filter(length => length > 0)
  return lengths.length > 0 ? Math.min(...lengths) : FALLBACK_SLOT_MINUTES
}

/** The week, day by day. Each day is `buildDayGrid` for one column: the caller. */
export function buildWeekGrid(input: BuildWeekGridInput): WeekSchedule {
  const schedule: WeekSchedule = {}

  for (const { isoDate } of input.weekDates) {
    schedule[isoDate] = buildDayGrid({
      date: isoDate,
      bookings: input.bookings,
      columns: [{ id: HOST_COLUMN, calUserId: input.calUserId }],
      slotMinutes: input.slotMinutes,
      timeZone: input.timeZone,
      availability: input.availability,
      offSlotEventTypeIds: input.offSlotEventTypeIds,
      privateEventTypeIds: input.privateEventTypeIds,
      businessHours: input.businessHours,
      bookingSeason: input.bookingSeason,
    })[HOST_COLUMN] ?? []
  }

  return schedule
}

/**
 * Each day's standing for the person whose week it is, keyed by ISO date — for the column header,
 * which labels a day rather than a slot.
 *
 * Days they are bookable right through are absent from the map rather than present and empty, so a
 * caller can test for the key. Same contract as `unavailabilityByColumn`, which does the work.
 */
export function unavailabilityByDay(input: BuildWeekGridInput): Record<CalendarDate, UnavailabilityMark> {
  const marks: Record<CalendarDate, UnavailabilityMark> = {}

  for (const { isoDate } of input.weekDates) {
    const mark = unavailabilityByColumn({
      date: isoDate,
      bookings: input.bookings,
      columns: [{ id: HOST_COLUMN, calUserId: input.calUserId }],
      slotMinutes: input.slotMinutes,
      timeZone: input.timeZone,
      availability: input.availability,
      businessHours: input.businessHours,
    })[HOST_COLUMN]

    if (mark) marks[isoDate] = mark
  }

  return marks
}
